import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, DatePicker, Divider, Form, Input, InputNumber, Modal, Select, Switch, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import { CheckOutlined, DeleteOutlined, EditOutlined, MailOutlined, SyncOutlined } from '@ant-design/icons';
import cx from 'classnames';
import dayjs from 'dayjs';
import {
    IBillingSystemOrdersListItem,
    INonRecurringCharges,
    INonRecurringChargesItem,
    IPostBillingOrder,
    NotificationType,
    billingSystemOrderStatuses,
} from '../../../interfaces';
import { useNonRecurringChargesList, useRouterStore, useUiIsSpinnerFound } from '../../../hooks';
import {
    addNotification,
    apiHandleErrorMessages,
    apiMessagesGetFormRulesFieldBlank,
    postBillingSystemInvoiceSendEmail,
} from '../../../middleware';
import {
    deleteBillingSystemOrder,
    getBillingSystemOrder,
    getGeneralNonRecurringChargesList,
    patchBillingStatusOrder,
    postBillingSystemOrderApprove,
    putBillingSystemOrder,
} from '../../../middleware';
import { disablePreviousDates, globalHexId, stringMoney } from '../../../instruments';
import { ITrButton, tB, tCustom, tI } from '../../../translate';
import { CardInfoToolBar, CardLayout, Catcher, FieldInfo, LinkComponent, cardLayoutOptions } from '../../../Components';
import Styles from '../../styles/m_itemCardStyles.less';
import BillingStyle from '../m_styles.less';

interface IModuleContent {
    nrcList: INonRecurringCharges;
    addNewItem: (id?: number | string, values?: INonRecurringChargesItem) => void;
}

const ModuleContent: React.FC<IModuleContent> = observer(({ nrcList, addNewItem }: IModuleContent) => {
    const onClick = (id?: number | string, values?: INonRecurringChargesItem) => {
        addNewItem(id, values);
    };

    return (
        <div style={{ padding: '10px' }}>
            <div className={Styles.mrcFormItem}>
                <h3>Nrc available for this company:</h3>
                <div
                    style={{
                        justifyContent: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                    }}
                >
                    {nrcList.map((value) => {
                        return (
                            <div className={Styles.mrcFormItemContainer} key={value.id} style={{ width: '250px' }}>
                                <span>{value.name}</span>
                                <div style={{ color: 'green' }}>EUR {value.amountCents}</div>
                                <Button onClick={() => onClick(value.id, value)} type={'primary'}>
                                    Add
                                </Button>
                            </div>
                        );
                    })}
                </div>
            </div>
            <Divider />
            <div
                style={{
                    justifyContent: 'center',
                    display: 'flex',
                }}
            >
                <div
                    style={{
                        border: '1px dashed white',
                        width: '250px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <h4 style={{ fontSize: 16 }}>New product</h4>
                    <Button key="submit" onClick={() => onClick()} style={{ width: '100%' }}>
                        {tB('Add')}
                    </Button>
                </div>
            </div>
        </div>
    );
});

export const BillingShowOrder: React.FC = observer(() => {
    const routerStore = useRouterStore();
    const [id, setId] = useState('');
    const [order, setOrder] = useState<IBillingSystemOrdersListItem>({ id });
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isApproveModalVisible, setIsApproveModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [invoiceDate, setInvoiceDate] = useState<dayjs.Dayjs | null>(null);
    const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(null);
    const [sendInvoice, setSendInvoice] = useState<boolean>(false);
    const [sendInvoiceOnDate, setSendInvoiceOnDate] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<billingSystemOrderStatuses | null>(null);
    const [editableProducts, setEditableProducts] = useState<any[]>([]);
    const [editableDiscount, setEditableDiscount] = useState<any>(null);
    const [editableDescription, setEditableDescription] = useState<string>('');
    const [editablePurchaseOrderNumber, setEditablePurchaseOrderNumber] = useState<string>('');
    const isUserTypingRef = useRef<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [generalNrcList, setGeneralNrcList] = useState<INonRecurringCharges>([]);
    const [isAddingDiscount, setIsAddingDiscount] = useState<boolean>(false);
    const nrcList = useNonRecurringChargesList();
    const isLoading = useUiIsSpinnerFound(['GET_BILLING_SYSTEM_ORDER', 'POST_INVOICE', 'PUT_BILLING_SYSTEM_ORDER']);
    const isOrderInFinalState = order.statusOverall === 'paid' || order.statusOverall === 'canceled';
    const isDraft = order.statusOverall === billingSystemOrderStatuses.DRAFT;
    const invoiceSentAt = order?.invoice?.sentAt;

    const sendInvoiceEmail = async () => {
        if (order.invoice?.id) {
            await postBillingSystemInvoiceSendEmail(order.invoice?.id.toString());
        }
    };

    useEffect(() => {
        const { params } = routerStore.routerState;
        if (params.id) {
            setId(params.id);
        }
    });

    const _getOrder = async (id: string): Promise<void> => {
        const { isError, order } = await getBillingSystemOrder(id);
        if (isError) {
            routerStore.goTo('BillingOrdersList');
        } else {
            setOrder(order);
        }
    };
    const refreshOrder = async (id: string): Promise<void> => {
        _getOrder(id);
        disableIsEditing();
        disableEditMode();
    };

    const enableEditMode = () => {
        setIsEditMode(true);
        isUserTypingRef.current = false;
        setIsAddingDiscount(false);

        const productsWithIds = (order.products || []).map((product, index) => ({
            ...product,
            id: `product-${index}`,
            nrcId: (product as any).id,
        }));

        setEditableProducts(productsWithIds);
        setEditableDiscount(order.discount || null);
        setEditablePurchaseOrderNumber(order.purchaseOrderNumber || '');
        setIsAddingDiscount(!!(order.discount && order.discount.amount));
    };

    const disableEditMode = () => {
        setIsEditMode(false);
        setEditableProducts([]);
        setEditableDiscount(null);
        setEditableDescription('');
        setEditablePurchaseOrderNumber('');
        setIsAddingDiscount(false);
    };

    useEffect(() => {
        if (id) {
            refreshOrder(id);
        }
    }, [id]);

    useEffect(() => {
        (async () => {
            const data = await getGeneralNonRecurringChargesList();
            setGeneralNrcList(data);
        })();
    }, []);

    useEffect(() => {
        if (isEditMode && !isUserTypingRef.current) {
            let autoGeneratedSummary = '';
            if (editableProducts.length === 0) {
                autoGeneratedSummary = '';
            } else if (editableProducts.length === 1) {
                autoGeneratedSummary = editableProducts[0].description;
            } else {
                autoGeneratedSummary = 'Several Items';
            }

            setEditableDescription(autoGeneratedSummary);
        }
    }, [editableProducts, isEditMode]);

    const options = Object.values(billingSystemOrderStatuses)
        .filter((status: string) => status !== 'draft')
        .map((status: string) => ({
            label: status.toUpperCase(),
            value: status,
            disabled: isOrderInFinalState || status === 'pending',
        }));

    const enableIsEditing = () => {
        setIsEditing(true);
    };

    const disableIsEditing = () => {
        setIsEditing(false);
    };

    const changeStatus = async (status: billingSystemOrderStatuses) => {
        setSelectedStatus(status);
        setIsModalVisible(true);
    };

    const handleStatusConfirm = async () => {
        if (!selectedStatus) return;

        const prevStatus = order.statusOverall;
        const resp = await patchBillingStatusOrder(order.id || '', selectedStatus);

        if (resp.isError) {
            apiHandleErrorMessages(resp || {});
            disableIsEditing();
            return;
        }

        refreshOrder(id);
        disableIsEditing();
        setIsModalVisible(false);
        setSelectedStatus(null);

        addNotification({
            type: NotificationType.SUCCESS,
            message: `Changed order status from ${prevStatus?.toUpperCase()} to ${selectedStatus.toUpperCase()} successfully`,
        });
    };

    const handleStatusCancel = () => {
        setIsModalVisible(false);
        setSelectedStatus(null);
    };

    const statusColor =
        order.statusOverall === 'pending'
            ? Styles.billingOrderStatusNamePending
            : order.statusOverall === 'paid'
            ? Styles.billingOrderStatusNamePaid
            : Styles.billingOrderStatusNameFailed;

    const handleDeleteDraft = async () => {
        if (!order.id) return;
        const { isError } = await deleteBillingSystemOrder(order.id);
        if (isError) {
            addNotification({ type: NotificationType.ERROR, message: 'Failed to delete order' });
            return;
        }
        addNotification({ type: NotificationType.SUCCESS, message: 'Order successfully deleted' });
        routerStore.goTo('BillingOrdersList');
    };

    const openDeleteModal = () => {
        setIsDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        await handleDeleteDraft();
        setIsDeleteModalVisible(false);
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
    };

    const onInvoiceDateChange = (date: dayjs.Dayjs | null) => {
        setInvoiceDate(date);
        if (date) {
            const paymentTerms = order.companyId?.overdue_period || 0;
            setDueDate(date.add(paymentTerms, 'day'));
        }
    };

    const onDueDateChange = (date: dayjs.Dayjs | null) => {
        setDueDate(date);
    };

    const onChangeSendInvoice = (e: CheckboxChangeEvent) => {
        setSendInvoice(e.target.checked);
        if (!e.target.checked) {
            setSendInvoiceOnDate(false);
        }
    };

    const onChangeSendInvoiceOnDate = (checked: boolean) => {
        setSendInvoiceOnDate(checked);
    };

    const handleApproveConfirm = async () => {
        if (!order.id) return;

        const body: any = {
            send_invoice: sendInvoice,
            send_invoice_on_date: sendInvoiceOnDate,
        };

        if (invoiceDate) {
            body.invoice_date = invoiceDate.format('YYYY-MM-DD');
        }

        if (dueDate) {
            body.due_date = dueDate.format('YYYY-MM-DD');
        }

        const { isError } = await postBillingSystemOrderApprove(order.id, body);
        if (isError) {
            addNotification({ type: NotificationType.ERROR, message: 'Failed to approve order' });
            return;
        }
        addNotification({ type: NotificationType.SUCCESS, message: 'Order approved' });
        setIsApproveModalVisible(false);
        setInvoiceDate(null);
        setDueDate(null);
        setSendInvoice(false);
        setSendInvoiceOnDate(false);
        refreshOrder(id);
    };

    const handleApproveCancel = () => {
        setIsApproveModalVisible(false);
        setInvoiceDate(null);
        setDueDate(null);
        setSendInvoice(false);
        setSendInvoiceOnDate(false);
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
    };

    const handleModalOpen = () => {
        setIsModalOpen(true);
    };

    const addProduct = (nrcId?: string | number, nrcValues?: INonRecurringChargesItem) => {
        const newProduct = {
            id: globalHexId.getId(),
            description: nrcValues?.name || 'New Product',
            units: 1,
            unitPrice: nrcValues?.amountCents || 0,
            nrcId,
        };
        setEditableProducts([...editableProducts, newProduct]);
        setIsModalOpen(false);
    };

    const updateProduct = (productId: string, field: string, value: any) => {
        const updatedProducts = editableProducts.map((product) =>
            product.id === productId ? { ...product, [field]: value } : product,
        );
        setEditableProducts(updatedProducts);
    };

    const removeProduct = (productId: string) => {
        const updatedProducts = editableProducts.filter((product) => product.id !== productId);
        setEditableProducts(updatedProducts);
    };

    const addDiscount = () => {
        setEditableDiscount({
            description: '',
            amount: '',
            amountType: 'currency',
        });
        setIsAddingDiscount(true);
    };

    const updateDiscount = (field: string, value: any) => {
        setEditableDiscount({ ...editableDiscount, [field]: value });
    };

    const removeDiscount = () => {
        setEditableDiscount(null);
        setIsAddingDiscount(false);
    };

    const descriptionOnChange = (e: any) => {
        isUserTypingRef.current = true;
        setEditableDescription(e.target.value);
    };

    const calculateTotal = () => {
        const productsTotal = editableProducts.reduce((total, product) => {
            return total + product.units * product.unitPrice;
        }, 0);

        let finalTotal = productsTotal;

        if (editableDiscount && editableDiscount.amount) {
            if (editableDiscount.amountType === 'percentage') {
                const discountAmount = (parseFloat(editableDiscount.amount) / 100) * productsTotal;
                finalTotal = Math.max(0, productsTotal - discountAmount);
            } else {
                const discountAmount = parseFloat(editableDiscount.amount) || 0;
                finalTotal = Math.max(0, productsTotal - discountAmount);
            }
        }

        return finalTotal;
    };

    const saveOrderChanges = async () => {
        try {
            const totalAmount = calculateTotal();
            const updatedOrder: IPostBillingOrder = {
                amount: totalAmount.toString(),
                description: editableDescription,
                products: editableProducts.map((product) => ({
                    description: product.description,
                    units: product.units,
                    unitPrice: product.unitPrice.toString(),
                })),
                discount: editableDiscount
                    ? {
                          description: editableDiscount.description,
                          amount: editableDiscount.amount,
                          amountType: editableDiscount.amountType,
                      }
                    : undefined,
                purchaseOrderNumber: editablePurchaseOrderNumber,
            };

            const resp = await putBillingSystemOrder(id, updatedOrder);

            if (resp.isError) {
                apiHandleErrorMessages(resp || {});
                return;
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Order updated successfully',
                duration: 3000,
            });

            refreshOrder(id);
        } catch (e) {
            console.log(e);
            addNotification({
                type: NotificationType.ERROR,
                message: 'Failed to update order',
                duration: 3000,
            });
        }
    };

    // Get names of products already in the order
    const usedNrcNames = editableProducts?.map((product: any) => product.description) || [];

    const filteredCompanyNrc = nrcList.filter((nrc: any) => !usedNrcNames.includes(nrc.name));
    const filteredGeneralNrc = generalNrcList.filter((nrc: any) => !usedNrcNames.includes(nrc.name));
    const filteredNrc = [...filteredCompanyNrc, ...filteredGeneralNrc];

    return (
        <Catcher>
            <article className={cx(Styles.main, Styles.billingShowOrder)}>
                <CardLayout idSpinners={['GET_ORDER']} titleLeft={`Order #${order.id}`}>
                    <FieldInfo title={'Company'}>
                        <LinkComponent
                            currentListComponent={'BillingCompaniesList'}
                            id={order.companyId?.id}
                            name={order.companyId?.name}
                        />
                    </FieldInfo>
                    <FieldInfo title={'Date'}>{order.updatedAt}</FieldInfo>
                    <FieldInfo title={'Due Date'}>{order.dueDate}</FieldInfo>
                    <FieldInfo title={'Bought By'}>
                        <LinkComponent
                            currentListComponent={'UsersList'}
                            id={order?.user?.id}
                            isLight
                            name={order?.user?.name}
                        />
                    </FieldInfo>
                    <FieldInfo title={'Transaction id'}>{order.transactionId}</FieldInfo>
                    <FieldInfo title={'Payment Method'}>{order.paymentTypeId}</FieldInfo>
                    <FieldInfo title={'Description'}>{order.description}</FieldInfo>
                    <FieldInfo title={'Cost'}>{stringMoney(order.amount, order.currencyId?.abbreviation)}</FieldInfo>
                    {order.purchaseOrderNumber ? (
                        <FieldInfo title={'Purchase Order Number'}>{order.purchaseOrderNumber}</FieldInfo>
                    ) : null}
                    <FieldInfo title={'Notes'}>{order.notes}</FieldInfo>
                    <Divider />
                    <div className={Styles.billingShowOrderStatus}>
                        <FieldInfo title={'Status'} className={Styles.billingShowOrderStatusName} />
                        {!isEditing || isOrderInFinalState ? (
                            <span className={statusColor}>{order.statusOverall?.toUpperCase()}</span>
                        ) : (
                            <Form.Item name={'statusOverall'} className={Styles.billingShowOrderStatusFormSelect}>
                                <Select options={options} onChange={changeStatus} defaultValue={order.statusOverall} />
                            </Form.Item>
                        )}
                        {!isOrderInFinalState && !isDraft ? (
                            <Button
                                icon={<EditOutlined />}
                                type={'link'}
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={isEditing ? disableIsEditing : enableIsEditing}
                            >
                                {isEditing ? 'Cancel edit status' : 'Edit status'}
                            </Button>
                        ) : null}
                    </div>
                    {order?.invoice?.id || order?.invoice?.pdfFileStatus ? (
                        <>
                            <Divider />
                            {order?.invoice?.id ? (
                                <FieldInfo title={'Invoice'}>
                                    <LinkComponent
                                        currentListComponent={'InvoicesList'}
                                        id={order.invoice.id.toString()}
                                        name={order?.invoice.code}
                                    />
                                </FieldInfo>
                            ) : null}
                            {order?.invoice?.pdfFileStatus ? (
                                <FieldInfo title={'Status'}>{order?.invoice?.pdfFileStatus}</FieldInfo>
                            ) : null}
                            {order?.invoice?.date ? (
                                <FieldInfo title={'Will be sent'}>
                                    {dayjs(order?.invoice?.date).format('YYYY-MM-DD')}
                                </FieldInfo>
                            ) : null}
                        </>
                    ) : null}
                    <CardInfoToolBar>
                        {isDraft ? (
                            <>
                                {isEditMode ? (
                                    <>
                                        <Button type={'primary'} onClick={disableEditMode} disabled={isLoading}>
                                            Cancel editing
                                        </Button>
                                        <Button
                                            type={'primary'}
                                            onClick={saveOrderChanges}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Save Changes
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            danger
                                            type={'primary'}
                                            icon={<DeleteOutlined />}
                                            onClick={openDeleteModal}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Delete
                                        </Button>
                                        <Button
                                            type={'primary'}
                                            icon={<EditOutlined />}
                                            onClick={enableEditMode}
                                            disabled={isLoading}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            type={'primary'}
                                            icon={<CheckOutlined />}
                                            onClick={() => setIsApproveModalVisible(true)}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Approve
                                        </Button>
                                    </>
                                )}
                            </>
                        ) : order?.invoice?.id ? (
                            <Tooltip title={invoiceSentAt && `${tI('Invoice already sent at')} ${invoiceSentAt}`}>
                                <Button
                                    onClick={() => sendInvoiceEmail()}
                                    icon={<MailOutlined />}
                                    disabled={isLoading || !!invoiceSentAt}
                                    loading={isLoading}
                                    className={Styles.billingShowOrderStatusRefreshButton}
                                >
                                    {tB('Send email invoice' as ITrButton)}
                                </Button>
                            </Tooltip>
                        ) : null}
                        <Button
                            type="primary"
                            onClick={() => refreshOrder(id)}
                            icon={<SyncOutlined />}
                            disabled={isLoading}
                            loading={isLoading}
                            className={Styles.billingShowOrderStatusRefreshButton}
                        >
                            {tB('Refresh')}
                        </Button>
                    </CardInfoToolBar>
                </CardLayout>
                <CardLayout
                    idSpinners={['GET_COMPANY_MONTHLY_PAYMENTS', 'GET_COMPANY']}
                    titleLeft={'Products'}
                    columnOption={cardLayoutOptions.mrcForm}
                >
                    {isEditMode ? (
                        <div className={BillingStyle.newOrderListForm}>
                            {editableProducts.map((product, index) => {
                                const total = product.units * product.unitPrice;
                                return (
                                    <div className={BillingStyle.newOrderListItem} key={product.id || index}>
                                        <Form.Item label={'Description'}>
                                            <Input
                                                value={product.description}
                                                onChange={(e) =>
                                                    updateProduct(product.id, 'description', e.target.value)
                                                }
                                                placeholder="Product description"
                                                className={BillingStyle.newOrderListInputs}
                                            />
                                        </Form.Item>
                                        <div className={BillingStyle.newOrderListItemUnits}>
                                            <Form.Item label={'Units'} style={{ width: '100%' }}>
                                                <InputNumber
                                                    value={product.units}
                                                    onChange={(value) => updateProduct(product.id, 'units', value)}
                                                    placeholder="Units"
                                                    className={BillingStyle.newOrderListInputs}
                                                    min={1}
                                                    step={1}
                                                />
                                            </Form.Item>
                                            <Form.Item label={'Amount'} style={{ width: '100%' }}>
                                                <InputNumber
                                                    value={product.unitPrice}
                                                    onChange={(value) => updateProduct(product.id, 'unitPrice', value)}
                                                    placeholder="Unit price"
                                                    className={BillingStyle.newOrderListInputs}
                                                    min={0.01}
                                                    step={1}
                                                    precision={2}
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className={BillingStyle.newOrderListItemTotal}>
                                            <Button danger onClick={() => removeProduct(product.id)}>
                                                Delete
                                            </Button>
                                            <div className={BillingStyle.total}>
                                                Price - {order.currencyId?.abbreviation || 'EUR'} {total.toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <Button
                                type="dashed"
                                onClick={filteredNrc.length ? handleModalOpen : () => addProduct()}
                                className={Styles.mrcAddButton}
                                disabled={!order.companyId}
                            >
                                + Add Product
                            </Button>
                            <Divider />
                            <div className={BillingStyle.newOrderSummary}>
                                <div className={BillingStyle.newOrderSummaryTitle}>Summary: </div>
                                <div>
                                    <Form.Item>
                                        <Input
                                            value={editableDescription}
                                            onChange={descriptionOnChange}
                                            placeholder={'Summary'}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div className={BillingStyle.newOrderSummary}>
                                <div className={BillingStyle.newOrderSummaryTitle}>PO Number: </div>
                                <div>
                                    <Form.Item>
                                        <Input
                                            value={editablePurchaseOrderNumber}
                                            onChange={(e) => setEditablePurchaseOrderNumber(e.target.value)}
                                            placeholder={'Purchase Order Number'}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div className={BillingStyle.newOrderSummaryDiscountContainer}>
                                {!isAddingDiscount && (
                                    <Button
                                        type="primary"
                                        onClick={addDiscount}
                                        disabled={editableProducts.length === 0}
                                    >
                                        + Add Discount
                                    </Button>
                                )}
                                {isAddingDiscount && (
                                    <div className={BillingStyle.newOrderSummaryDiscountItem}>
                                        <Form.Item
                                            label={'Discount description'}
                                            required
                                            rules={apiMessagesGetFormRulesFieldBlank()}
                                        >
                                            <Input
                                                value={editableDiscount?.description || ''}
                                                onChange={(e) => updateDiscount('description', e.target.value)}
                                                className={BillingStyle.newOrderSummaryDiscountInputs}
                                            />
                                        </Form.Item>
                                        <Form.Item label={'Units'} required rules={apiMessagesGetFormRulesFieldBlank()}>
                                            <Select
                                                value={editableDiscount?.amountType || 'currency'}
                                                onChange={(value) => updateDiscount('amountType', value)}
                                                className={BillingStyle.newOrderSummaryDiscountInputs}
                                                allowClear={false}
                                                placeholder={'Unit'}
                                            >
                                                <Select.Option value="currency">
                                                    {order.currencyId?.abbreviation || 'EUR'}
                                                </Select.Option>
                                                <Select.Option value="percentage">%</Select.Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            label={'Amount'}
                                            required
                                            rules={apiMessagesGetFormRulesFieldBlank()}
                                        >
                                            <InputNumber
                                                value={editableDiscount?.amount || ''}
                                                onChange={(value) => updateDiscount('amount', value)}
                                                className={BillingStyle.newOrderSummaryDiscountInputs}
                                                min={1}
                                                step={1}
                                            />
                                        </Form.Item>
                                        <div className={BillingStyle.newOrderSummaryDiscountItemTotal}>
                                            <Button danger onClick={removeDiscount}>
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <div className={BillingStyle.newOrderSummaryDiscountTotal}>
                                    TOTAL: {order.currencyId?.abbreviation || 'EUR'} {calculateTotal().toFixed(2)}
                                </div>
                            </div>
                        </div>
                    ) : order?.products?.length ? (
                        <div className={Styles.mrcFormItem}>
                            {order?.products.map((value, index) => {
                                const total = value.units * value.unitPrice;
                                const unitPriceParsed = Number(value.unitPrice);

                                return (
                                    <div className={Styles.mrcFormItemContainer} key={index}>
                                        <FieldInfo title={'Description'}>{value.description}</FieldInfo>
                                        <FieldInfo title={'Units'}>{value.units}</FieldInfo>
                                        <FieldInfo title={'Price'}>
                                            {order.currencyId?.abbreviation} {unitPriceParsed.toFixed(2)}
                                        </FieldInfo>
                                        <Divider />
                                        <div style={{ color: 'green', fontSize: '16px' }}>
                                            Total: {order.currencyId?.abbreviation} {total.toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })}
                            {order.discount?.amount && (
                                <div className={Styles.mrcFormItemContainer}>
                                    <FieldInfo title={'Description'}>{order.discount.description}</FieldInfo>
                                    <FieldInfo title={'Amount'}>
                                        {order.discount.amount}{' '}
                                        {order.discount.amountType === 'percentage'
                                            ? '%'
                                            : order.currencyId?.abbreviation}
                                    </FieldInfo>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={Styles.mrcFormEmptyContainer}>
                            <p className={Styles.mrcFormEmptyText}>No Products</p>
                        </div>
                    )}
                </CardLayout>
            </article>
            <Modal
                title={tCustom('Confirm Status Change', {})}
                wrapClassName={'modal'}
                open={isModalVisible}
                onOk={handleStatusConfirm}
                onCancel={handleStatusCancel}
                okText={tB('Confirm')}
                cancelText={tB('Cancel')}
                // ToDo: 28.11.2025 -  need refactoring
            >
                <p>
                    {tCustom('Are you sure you want to change the order status?', {
                        prevStatus: order.statusOverall?.toUpperCase(),
                        newStatus: selectedStatus?.toUpperCase(),
                    })}
                </p>
            </Modal>
            <Modal
                title={'Approve Order'} // ToDo: 25.11.2025 -  need translate
                wrapClassName={'modal'}
                open={isApproveModalVisible}
                onOk={handleApproveConfirm}
                onCancel={handleApproveCancel}
                okText={tB('Confirm')}
                cancelText={tB('Cancel')}
            >
                <p>
                    The draft order will be approved and moved to the <b>PENDING</b> status.
                </p>
                <Form layout="vertical">
                    <Form.Item required label="Invoice Date">
                        {/*// ToDo: 25.11.2025 - need translate*/}
                        <DatePicker
                            value={invoiceDate}
                            allowClear={false}
                            onChange={onInvoiceDateChange}
                            disabledDate={disablePreviousDates}
                        />
                    </Form.Item>
                    <Form.Item required label="Due Date">
                        {/*// ToDo: 25.11.2025 - need translate*/}
                        <DatePicker
                            value={dueDate}
                            allowClear={false}
                            onChange={onDueDateChange}
                            disabledDate={disablePreviousDates}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Checkbox checked={sendInvoice} disabled={!Boolean(invoiceDate)} onChange={onChangeSendInvoice}>
                            {/*// ToDo: 25.11.2025 - need translate*/}
                            Send Invoice
                        </Checkbox>
                    </Form.Item>
                    {sendInvoice && (
                        <Form.Item>
                            <div className={BillingStyle.newOrderDateField}>
                                <Switch
                                    unCheckedChildren={'Now'} // ToDo: 25.11.2025 - need translate
                                    checkedChildren={'On Date'} // ToDo: 25.11.2025 - need translate
                                    checked={sendInvoiceOnDate}
                                    onChange={onChangeSendInvoiceOnDate}
                                />
                                {Boolean(sendInvoiceOnDate) && Boolean(invoiceDate) ? (
                                    <div className={BillingStyle.newOrderDeducedInvoiceNumber}>
                                        {/*// ToDo: 25.11.2025 - need translate*/}
                                        Will be sent on: {invoiceDate?.format('YYYY-MM-DD')}
                                    </div>
                                ) : null}
                            </div>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
            <Modal
                title={'Delete Order'} // ToDo: 25.11.2025 -  need translate
                wrapClassName={'modal'}
                open={isDeleteModalVisible}
                onOk={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                okText={tB('Delete')}
                cancelText={tB('Cancel')}
                okButtonProps={{ danger: true }}
            >
                <p>Are you sure you want to delete this draft order? This action cannot be undone.</p>
            </Modal>
            <Modal
                title="Select product"
                open={isModalOpen}
                wrapClassName={'modal'}
                onCancel={handleModalCancel}
                footer={[
                    <Button key="back" type="primary" onClick={handleModalCancel}>
                        {tB('Cancel')}
                    </Button>,
                ]}
                width={700}
            >
                <ModuleContent nrcList={filteredNrc} addNewItem={addProduct} />
            </Modal>
        </Catcher>
    );
});
